import React, { createContext, useContext, useState, useEffect } from 'react';
import { showToast } from '@/utils/toast';
import { useAuthState } from '../hooks/useAuthState';
import { supabase, handleSupabaseError } from '../config/supabase';
import { Task } from '../types/task';
import { mapTaskFromDb } from '../utils/transforms';

interface TaskContextType {
  tasks: Task[];
  loading: boolean;
  addTask: (task: Omit<Task, 'id' | 'createdAt'>) => Promise<void>;
  updateTask: (taskId: string, data: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
}

export const TaskContext = createContext<TaskContextType | undefined>(undefined);


export const TaskProvider = ({ children }: { children: React.ReactNode }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthState();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadTasks = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTasks((data || []).map(mapTaskFromDb));
      } catch (error) {
        console.error('Error loading tasks:', error);
        handleSupabaseError(error);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [user?.id]);

  const addTask = async (taskData: Omit<Task, 'id' | 'createdAt'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          user_id: user.id,
          project_id: taskData.projectId,
          title: taskData.title,
          description: taskData.description,
          status: taskData.completed ? 'completed' : 'pending',
          priority: taskData.priority || 'medium',
          estimated_pomodoros: taskData.estimatedPomodoros || 1,
          completed_pomodoros: taskData.completedPomodoros || 0,
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [mapTaskFromDb(data), ...prev]);
      showToast.success('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const updateTask = async (id: string, taskData: Partial<Task>) => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({
          title: taskData.title,
          description: taskData.description,
          status: taskData.completed ? 'completed' : 'pending',
          priority: taskData.priority,
          estimated_pomodoros: taskData.estimatedPomodoros,
          completed_pomodoros: taskData.completedPomodoros,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => prev.map(t => t.id === id ? mapTaskFromDb(data) : t));
      showToast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTasks(prev => prev.filter(t => t.id !== id));
      showToast.success('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  return (
    <TaskContext.Provider value={{ tasks, loading, addTask, updateTask, deleteTask }}>
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};
